/**
 * PostHog Implementation (web)
 *
 * Uses posthog-js — the browser SDK that powers PostHog **Web Analytics**.
 * The React Native SDK (libs/posthog.ts) emits bare events that Web Analytics
 * can't aggregate (no $host / $browser / $os / $device_type / referrer / GeoIP,
 * and $lib = posthog-react-native). posthog-js auto-enriches every event with
 * those web dimensions, so the Web Analytics dashboard populates the same way it
 * does for Sparta (which captures `$pageview` through this same SDK via
 * @vidahealth/utilos `getPosthogClient`).
 *
 * This file should only be imported by analytics.ts / PostHogClientProvider.web.
 */

import Constants from 'expo-constants';
import { decamelizeKeys } from 'humps';
import posthogJs from 'posthog-js';

import { getNativeAppClientInfo } from '@/modules/clientContext';
import type { User } from '@/types/user';

// Module-scoped so the init guard + "is enabled" check survive remounts
// (StrictMode, Fast Refresh). posthog-js is itself a singleton, so `client`
// doubles as the "has init run?" flag — functions below no-op until it's set.
let client: typeof posthogJs | null = null;

const appName = Constants.expoConfig?.name || 'Vida Onboarding';
const environment = process.env.EXPO_PUBLIC_VIDA_ENV || (__DEV__ ? 'development' : 'production');
const version = Constants.expoConfig?.version || '1.0.0';

/**
 * Initialize posthog-js. Called client-side from PostHogClientProvider.web so
 * `window` is guaranteed to exist (mirrors DatadogProvider.web's useEffect init).
 * Config mirrors utilos `setupPosthog`: manual pageviews (we capture them in
 * trackScreen), pageleave on, person_profiles 'always' so anonymous sessions are
 * tracked before identify(), input masking for session replay.
 */
export const initPostHogWeb = (): void => {
  if (client) return;

  const apiKey = process.env.EXPO_PUBLIC_POSTHOG_API_KEY;
  const enabled = process.env.EXPO_PUBLIC_POSTHOG_ENABLED === 'true';
  if (!apiKey || !enabled) {
    console.log('PostHog Web: Disabled (missing key or EXPO_PUBLIC_POSTHOG_ENABLED !== "true")');
    return;
  }

  try {
    // posthog-js config option names are snake_case (the SDK's own API) — left
    // as-is here. Mirrors utilos setupPosthog.
    posthogJs.init(apiKey, {
      api_host: process.env.EXPO_PUBLIC_POSTHOG_HOST || '',
      capture_pageview: false, // we capture $pageview manually in trackScreen
      capture_pageleave: true,
      // 'always' so anonymous sessions are tracked before identify() (e.g. SSO
      // users who skip the onboarding flow) — matches utilos setupPosthog.
      person_profiles: 'always',
      // Intentional parity with Sparta/utilos setupPosthog — we track
      // interactions via explicit trackEvent calls, not autocapture. (Native
      // enables captureTouches in PostHogClientProvider.tsx; the asymmetry is
      // deliberate.)
      autocapture: false,
      mask_all_text: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        maskInputOptions: {
          password: true,
          email: true,
        },
      },
    });

    // Global context properties — mirrors the RN client's posthog.register().
    // The embed launch params (Sparta's vida_client / vida_client_version) are
    // registered as super-properties so they ride every event app-wide,
    // including anonymous sessions (Sparta's "registered app-wide at analytics
    // init"). Author camelCase + decamelize at the boundary → vida_client /
    // vida_client_version; only present when launched embedded.
    const { vidaClient, vidaClientVersion } = getNativeAppClientInfo();
    posthogJs.register({
      service: appName,
      application: appName,
      environment,
      release: version,
      platform: 'web',
      ...(vidaClient
        ? (decamelizeKeys({ vidaClient, vidaClientVersion }) as Record<string, unknown>)
        : {}),
    });

    client = posthogJs;
  } catch (error) {
    console.error('PostHog Web: Failed to initialize', error);
  }
};

/**
 * Returns the initialized client, lazily initializing if a capture/identify
 * fires before PostHogClientProvider.web's mount effect runs. React flushes
 * descendant passive effects before ancestor ones, so AutoCapture's first
 * $pageview (the core Web Analytics event) would otherwise hit a null client and
 * be dropped. Effects only run in the browser, so `window` exists here; the
 * guard keeps any SSR / pre-mount caller a safe no-op.
 */
const ensureClient = (): typeof posthogJs | null => {
  if (!client && typeof window !== 'undefined') initPostHogWeb();
  return client;
};

/**
 * Initialize PostHog for a logged-in user
 */
export const initializeUser = (user: User): void => {
  const c = ensureClient();
  if (!c) return;
  try {
    // Reset the flag-load cache on an actual identity change so the new user's
    // flags reload (mirrors native posthog.ts). posthog-js's identify() reloads
    // flags internally; this just keeps our dedupe state in sync.
    if (_lastUserId !== user.uuid) {
      _lastUserId = user.uuid;
      resetFlagsState();
    }
    // Author camelCase; decamelize at the PostHog boundary so dashboards see the
    // snake_case keys Sparta established.
    c.identify(user.uuid, decamelizeKeys({ isTester: user.isTester }) as Record<string, any>);
    c.register({ uuid: user.uuid });
  } catch (error) {
    console.error('PostHog Web: Failed to initialize user', error);
  }
};

/**
 * Reset PostHog user (logout)
 */
export const resetUser = (): void => {
  const c = ensureClient();
  if (!c) return;
  try {
    _lastUserId = null;
    resetFlagsState();
    c.reset();
  } catch (error) {
    console.error('PostHog Web: Failed to reset user', error);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>): void => {
  const c = ensureClient();
  if (!c) return;
  try {
    c.capture(
      eventName,
      properties ? (decamelizeKeys(properties) as Record<string, any>) : undefined
    );
  } catch (error) {
    console.error('PostHog Web: Failed to track event', error);
  }
};

/**
 * Track a screen view. On web this is a $pageview — posthog-js auto-enriches it
 * with $current_url / $host / $pathname / $browser / $os / $device_type /
 * $referrer + GeoIP, so we intentionally do NOT pass those (mirrors Sparta's
 * bare `capture('$pageview')`). Extra props (segments / path) ride along as
 * custom properties.
 */
export const trackScreen = (_screenName: string, properties?: Record<string, any>): void => {
  const c = ensureClient();
  if (!c) return;
  try {
    c.capture(
      '$pageview',
      properties ? (decamelizeKeys(properties) as Record<string, any>) : undefined
    );
  } catch (error) {
    console.error('PostHog Web: Failed to track screen', error);
  }
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>): void => {
  const c = ensureClient();
  if (!c) return;
  try {
    c.setPersonProperties(decamelizeKeys(properties) as Record<string, any>);
  } catch (error) {
    console.error('PostHog Web: Failed to set user properties', error);
  }
};

/**
 * Remove a user property
 */
export const removeUserProperty = (key: string): void => {
  const c = ensureClient();
  if (!c) return;
  try {
    c.capture('$unset', { $unset: [key] });
  } catch (error) {
    console.error('PostHog Web: Failed to remove user property', error);
  }
};

// Dedupes concurrent flag loads — one reload in flight at a time, mirroring the
// native posthog.ts pattern (_reloadPromise + _flagsLoaded). posthog-js has no
// reloadFeatureFlagsAsync(), so the promise is built from the onFeatureFlags
// callback, which fires once flags load OR error. _flagsLoaded short-circuits
// later cache-miss reads; resetFlagsState() clears it on identity change so a
// new user's flags reload.
let _flagsReadyPromise: Promise<void> | null = null;
let _flagsLoaded = false;
let _lastUserId: string | null = null;

const resetFlagsState = (): void => {
  _flagsReadyPromise = null;
  _flagsLoaded = false;
};

/**
 * Resolve once feature flags have loaded (or errored). Concurrent callers share
 * one reload. The Promise body is wrapped in try/catch so a synchronous throw
 * from onFeatureFlags / reloadFeatureFlags settles to the default instead of
 * stranding every awaiter (an unhandled throw inside a Promise executor would
 * leave the promise — and all callers — pending forever).
 */
const ensureFlagsLoaded = (): Promise<void> => {
  if (_flagsLoaded) return Promise.resolve();
  const c = ensureClient();
  if (!c) return Promise.resolve();
  if (!_flagsReadyPromise) {
    _flagsReadyPromise = new Promise<void>((resolve) => {
      let off: (() => void) | undefined;
      let settled = false;
      try {
        off = c.onFeatureFlags((_flags, _variants, context) => {
          // posthog-js invokes this callback *synchronously* (inside
          // onFeatureFlags) when flags are already cached — i.e. before the
          // `off = …` assignment below has completed, so `off` is still
          // undefined here. Calling it directly would no-op and leak the
          // listener, so we defer the unsubscribe to a microtask that runs after
          // assignment, by which point `off` holds the real teardown fn. (This
          // is also why `off` is a separately-declared `let`, not a `const` on
          // the assignment line — a same-line const would put `off` in its
          // temporal dead zone and throw on read; the original bug.) The
          // `settled` guard keeps a later async re-fire from double-resolving or
          // unsubscribing twice.
          if (settled) return;
          settled = true;
          // Mark loaded only on a clean load so an errored load retries next
          // call. The synchronous (already-cached) path passes no `context`,
          // which reads as a clean load — correct, since cached flags are loaded.
          if (!context?.errorsLoading) _flagsLoaded = true;
          queueMicrotask(() => off?.());
          resolve();
        });
        c.reloadFeatureFlags();
      } catch (error) {
        console.error('PostHog Web: Failed to reload feature flags', error);
        // If onFeatureFlags registered before reloadFeatureFlags threw, the
        // handler is still subscribed and (since we never settle) would leak.
        // Tear it down on the same deferred-microtask basis as the happy path.
        if (!settled) {
          settled = true;
          queueMicrotask(() => off?.());
        }
        resolve();
      }
    }).finally(() => {
      _flagsReadyPromise = null;
    });
  }
  return _flagsReadyPromise;
};

/**
 * Get feature flag value
 */
export const getFeatureFlag = async (key: string): Promise<string | boolean | undefined> => {
  const c = ensureClient();
  if (!c) return undefined;
  try {
    const cached = c.getFeatureFlag(key);
    if (cached !== undefined) return cached;
    await ensureFlagsLoaded();
    return c.getFeatureFlag(key);
  } catch (error) {
    console.error('PostHog Web: Failed to get feature flag', error);
    return undefined;
  }
};

/**
 * Subscribe to feature-flag (re)loads. The callback fires whenever PostHog loads
 * or reloads flags — including after `identify` re-evaluates them for the newly
 * identified distinct_id. Returns an unsubscribe fn. Lets a flag read that ran
 * before identify refresh once the identified user's flags land.
 */
export const onFeatureFlags = (callback: () => void): (() => void) => {
  const c = ensureClient();
  if (!c) {
    // Surface the silent failure mode: with no client the caller gets a noop
    // unsubscribe and never re-reads on reload (flags would just never refresh
    // this session). Shouldn't happen — ensureClient lazy-inits when `window`
    // exists — but log it so a regression is observable rather than invisible.
    console.error('PostHog Web: onFeatureFlags called with no client; flags will not refresh');
    return () => {};
  }
  try {
    return c.onFeatureFlags(() => callback());
  } catch (error) {
    console.error('PostHog Web: Failed to subscribe to feature flags', error);
    return () => {};
  }
};

/**
 * Check if feature flag is enabled
 */
export const isFeatureFlagEnabled = async (key: string): Promise<boolean> => {
  const c = ensureClient();
  if (!c) return false;
  try {
    const cached = c.isFeatureEnabled(key);
    if (cached !== undefined) return cached;
    await ensureFlagsLoaded();
    return c.isFeatureEnabled(key) ?? false;
  } catch (error) {
    console.error('PostHog Web: Failed to check feature flag', error);
    return false;
  }
};
